import type { TFunction } from "i18next";
import type { TeamPlayer } from "../../types";
import { Plus } from "../icons";
import PlayerChip from "./PlayerChip";
import type { LineupEditingState } from "./StartersArea";
import type { LineupChip, UseLineupDndResult } from "./useLineupDnd";

interface BenchGridProps {
  teamType: "home" | "away";
  chips: LineupChip[];
  dnd: UseLineupDndResult;
  readOnly: boolean;
  editingPlayer: LineupEditingState | null;
  onStartEdit: (teamType: "home" | "away", player: TeamPlayer) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditJerseyChange: (value: number) => void;
  onEditNameChange: (value: string) => void;
  /** How many "bolita" placeholder chips to render at the end of the bench
   * strip (already clamped by the caller to the remaining matchday-squad
   * headroom). Zero (or `onOpenQuickCreate` omitted) renders none. */
  placeholderCount: number;
  /** Opens the existing QuickCreatePlayerModal for this team; omitted by
   * callers that don't support inline quick-create from the bench. */
  onOpenQuickCreate?: () => void;
  /** PMA-COCK-171: true in the live bench-only panel — the starters zone is
   * locked, so the primary tap-toggle (whose only target from the bench is
   * "starters") is disabled for every bench chip. The separate "remove from
   * squad" affordance is unaffected (still governed by `lockedPlayerIds`). */
  benchOnly?: boolean;
  /** PMA-COCK-171: bench players who have already entered the field via a
   * Substitution event — locked (no toggle, no remove, no edit) so "quitar
   * de la convocatoria" can't pull an active player off the matchday squad. */
  lockedPlayerIds?: Set<string>;
  t: TFunction;
}

export default function BenchGrid({
  teamType,
  chips,
  dnd,
  readOnly,
  editingPlayer,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditJerseyChange,
  onEditNameChange,
  placeholderCount,
  onOpenQuickCreate,
  benchOnly,
  lockedPlayerIds,
  t,
}: BenchGridProps) {
  const zoneProps = dnd.zoneProps("bench");
  const isDropHover = dnd.dragOverZone === "bench";

  return (
    <div>
      <h4 className="font-medium mb-3">{t("substitutes")}</h4>
      <div
        {...zoneProps}
        data-testid={`${teamType}-substitutes-candidates`}
        className={`space-y-2 min-h-[3rem] rounded-lg p-1 transition-colors md:max-h-[26rem] md:overflow-y-auto md:pr-1 lg:max-h-[32rem] ${
          isDropHover ? "bg-emerald-50 ring-2 ring-emerald-300" : ""
        }`}
      >
        {chips.length === 0 && (
          <p className="text-xs text-gray-400 italic p-2">{t("noPlayers")}</p>
        )}
        {chips.map(({ player, selectable }) => {
          const isEditing =
            editingPlayer?.teamType === teamType &&
            editingPlayer?.column === "substitutes" &&
            editingPlayer?.playerId === player.player_id;
          const isPlayerLocked = Boolean(
            lockedPlayerIds?.has(player.player_id),
          );
          const isChipReadOnly = readOnly || isPlayerLocked;
          return (
            <PlayerChip
              key={player.player_id}
              player={player}
              zone="bench"
              readOnly={isChipReadOnly}
              toggleDisabled={!selectable || Boolean(benchOnly)}
              isDragging={dnd.draggingPlayerId === player.player_id}
              dragProps={dnd.chipProps(player, "bench")}
              isEditing={Boolean(isEditing)}
              editValues={
                isEditing && editingPlayer
                  ? {
                      jersey_number: editingPlayer.jersey_number,
                      player_name: editingPlayer.player_name,
                    }
                  : null
              }
              onStartEdit={() => onStartEdit(teamType, player)}
              onCancelEdit={onCancelEdit}
              onSaveEdit={onSaveEdit}
              onEditJerseyChange={onEditJerseyChange}
              onEditNameChange={onEditNameChange}
              onRemove={
                isChipReadOnly
                  ? undefined
                  : () => dnd.assign(player, "excluded")
              }
              t={t}
            />
          );
        })}
      </div>
      {!readOnly && onOpenQuickCreate && placeholderCount > 0 && (
        <div
          className="flex items-center gap-2 mt-2"
          data-testid={`${teamType}-lineup-placeholders`}
        >
          {Array.from({ length: placeholderCount }).map((_, index) => (
            <button
              key={`placeholder-${index}`}
              type="button"
              onClick={onOpenQuickCreate}
              aria-label={t("lineupBoard.addPlaceholderPlayer", {
                defaultValue: "Add a quick player",
              })}
              title={t("lineupBoard.addPlaceholderPlayer", {
                defaultValue: "Add a quick player",
              })}
              data-testid={`${teamType}-lineup-placeholder-${index}`}
              className="flex items-center justify-center h-10 w-10 rounded-full border-2 border-dashed border-gray-300 text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
